import {
  getOwnerDocument,
  useObjectRef,
  useUpdateEffect,
  useViewportSize,
} from '@react-aria/utils';
import { once } from 'lodash-es';
import {
  AnimatePresence,
  animate,
  motion,
  useMotionTemplate,
  useMotionValue,
  useTransform,
} from 'motion/react';
import type { ForwardedRef, RefObject } from 'react';
import { forwardRef, useCallback, useReducer, useRef, useState } from 'react';
import type { ModalOverlayProps } from 'react-aria-components';
import {
  composeRenderProps,
  Modal,
  ModalContext,
  ModalOverlay,
} from 'react-aria-components';

import {
  clamp,
  findNearest,
  isAscending,
  lerp,
  sequence,
} from '@infinex-private/ts-utils/number';
import type { UnsafeStyleProps } from '@infinex/ui-styles';
import { cn, useContextProps } from '@infinex/ui-styles';
import { querySelector } from '@infinex/ui-utils/dom';
import { useDebouncedValue } from '@infinex/ui-utils/react';

import {
  DIALOG_VIRTUAL_KEYBOARD_OFFSET_ID,
  SHEET_INSET,
  SheetVars,
} from './constants';
import type { ModalPrimitiveProps } from './ModalPrimitive';
import { getModalLayoutClasses } from './ModalPrimitive';
import type { TrayState } from './TrayPrimitive';
import { getUnmountY, useTrayState } from './TrayPrimitive';
import { useScrollIntervention } from './useScrollIntervention';

const MotionModal = motion.create(Modal);
const MotionModalOverlay = motion.create(ModalOverlay);

const PHYSICAL_PROPERTIES = { mass: 0.8, damping: 26, stiffness: 280 };

export type SheetPrimitiveProps = {
  /**
   * Sheets resize according to their detents, which are particular points at
   * which a sheet naturally rests. Detents, between 0 and 1, represent the
   * height relative to the viewport.
   *
   * @default [1]
   */
  detents?: number[];
  /**
   * When providing multiple detents, you may wish to control the snap behaviour. When `"mandatory"`,
   * the sheet will snap to the adjacent detent regardless of the gesture's inertia or distance.
   *
   * @default 'proximity'
   */
  snap?: 'proximity' | 'mandatory';
} & Omit<ModalOverlayProps, 'onAnimationStart' | 'className' | 'style'> &
  Pick<ModalPrimitiveProps, 'onOpenComplete' | 'onCloseComplete'> &
  UnsafeStyleProps;

/**
 * Styled sheet overlay. Prefer `<Dialog mobileType="sheet" />` in most cases.
 */
export const SheetPrimitive = forwardRef(function SheetPrimitive(
  props: SheetPrimitiveProps,
  forwardedRef: ForwardedRef<HTMLDivElement>
) {
  [props, forwardedRef] = useContextProps(props, forwardedRef, ModalContext);
  const domRef = useObjectRef(forwardedRef);
  const state = useTrayState(props);

  return (
    <AnimatePresence>
      {state.isOpen && (
        <SheetInner key="sheet" sheetRef={domRef} state={state} {...props} />
      )}
    </AnimatePresence>
  );
});

type SheetInnerProps = {
  sheetRef: RefObject<HTMLDivElement | null>;
  state: TrayState;
} & SheetPrimitiveProps;

function SheetInner(props: SheetInnerProps) {
  const {
    className,
    detents = [1],
    isDismissable,
    isKeyboardDismissDisabled,
    isOpen: _unusedIsOpen,
    onOpenChange,
    sheetRef,
    snap = 'proximity',
    state,
    style,
    ...otherProps
  } = props;

  const overlayRef = useRef<HTMLDivElement>(null);
  const [isTouchSafe, setTouchSafe] = useState(false);
  const { height: viewportHeight } = useViewportSize();
  const modalLayoutClasses = getModalLayoutClasses();

  // the `onAnimationComplete` event is called after all sorts of animations
  // e.g. elastic snap back to the initial position after dragging up. these
  // guards ensure consumer handlers are only called when appropriate
  const onOpenComplete = props.onOpenComplete && once(props.onOpenComplete);
  const onCloseComplete = props.onCloseComplete && once(props.onCloseComplete);

  // Calculate drag points relative to the viewport height (range max). The
  // secondary constraint (absolute max) is required since the last detent
  // cannot exceed the top inset.
  const dragMax = viewportHeight - SHEET_INSET;
  const { dragPoints, snapPoints } = getPoints(
    normaliseDetents(detents),
    [0, viewportHeight],
    dragMax
  );

  // There is a drag point for each provided detent, plus the closed state.
  // Instead of height, a drag point's value is the distance from the top of the
  // viewport to the top of the sheet, in pixels.
  const getDragPoint = useCallback(
    (index: number) => dragPoints[clamp(index, 0, dragPoints.length - 1)],
    [dragPoints]
  );
  const getSnapPoint = useCallback(
    (index: number) => snapPoints[clamp(index, 0, snapPoints.length - 1)],
    [snapPoints]
  );

  const firstIndex = 1; // skip over the zero-th (closed) detent
  const lastIndex = dragPoints.length - 1;
  const [currentIndex, setCurrentIndex] = useState<number>(firstIndex);
  const allowScroll = currentIndex === lastIndex;

  useScrollIntervention(sheetRef, { allowScroll });
  const startY = getDragPoint(firstIndex);
  const dragY = useMotionValue(startY);
  const bgOpacity = useTransform(
    dragY,
    [getDragPoint(lastIndex), startY, getDragPoint(0)],
    [0.8, 0.6, 0]
  );
  const overlayBg = useMotionTemplate`rgba(0, 0, 0, ${bgOpacity})`;

  // initialise the vars (updated in drag-end)
  const [detentHeight, setDetentHeight] = useState(getSnapPoint(currentIndex));
  const [maxHeight, setMaxHeight] = useState(getSnapPoint(lastIndex));

  const [_, forceUpdate] = useReducer(() => ({}), {});
  const initialHeightRef = useRef(viewportHeight);
  const debouncedHeight = useDebouncedValue(viewportHeight, 150);
  useUpdateEffect(() => {
    // assume that:
    // - the virtual keyboard is larger than 100px
    // - expanded browser chrome is smaller than 100px
    const heuristicThreshold = 100;
    const initialHeight = initialHeightRef.current;
    const heightDiff = Math.max(initialHeight - debouncedHeight, 0);

    const vkOffsetElement = querySelector(
      sheetRef.current,
      `#${DIALOG_VIRTUAL_KEYBOARD_OFFSET_ID}`
    );

    if (vkOffsetElement) {
      // A large height diff indicates the virtual keyboard has been opened—
      // pad the sheet content to compensate for the keyboard's height
      if (heightDiff > heuristicThreshold) {
        const footer = querySelector(sheetRef.current, '[slot="footer"]');
        vkOffsetElement.style.height = `${heightDiff - (footer?.clientHeight || 0)}px`;
      } else {
        vkOffsetElement.style.height = '0px';

        if (initialHeight !== debouncedHeight) {
          setMaxHeight(getSnapPoint(lastIndex));
          initialHeightRef.current = debouncedHeight;
          forceUpdate();
        }
      }
    }

    // A small height diff is a change to the browser's chrome, typically showing the address bar—
    // update the appropriate height variables
    if (heightDiff < heuristicThreshold) {
      setMaxHeight(getSnapPoint(lastIndex));
    }
  }, [debouncedHeight]);

  return (
    <MotionModalOverlay
      ref={overlayRef}
      // force open: let `AnimatePresence` handle enter/exit
      isOpen
      onOpenChange={onOpenChange}
      // Touch safe pattern circumvents an issue where `AnimatePresence` doesn't
      // "release" the exiting component when close is called before the open
      // animation completes. The failed unmount is an awful experience: it
      // looks like the dialog is gone but it's just out of view, meanwhile the
      // app is oblivious to user gestures.
      isDismissable={isDismissable && isTouchSafe}
      isKeyboardDismissDisabled={!isTouchSafe || isKeyboardDismissDisabled}
      className={modalLayoutClasses.overlay}
      style={{ backgroundColor: overlayBg }}
    >
      <div className={modalLayoutClasses.wrapper}>
        <MotionModal
          {...otherProps}
          ref={sheetRef}
          initial="hidden"
          animate="visible"
          exit="unmount"
          variants={{
            hidden: { y: getDragPoint(0) },
            visible: { y: startY },
            unmount: { y: getUnmountY(getDragPoint(0)) },
          }}
          drag="y"
          dragConstraints={{
            top: getDragPoint(lastIndex),
            bottom: isDismissable ? 0 : getDragPoint(1),
          }}
          dragElastic={{
            top: 0.05,
            bottom: isDismissable ? 1 : 0.1,
          }}
          dragTransition={{
            bounceDamping: PHYSICAL_PROPERTIES.damping,
            bounceStiffness: PHYSICAL_PROPERTIES.stiffness,
          }}
          transition={{ type: 'spring', ...PHYSICAL_PROPERTIES }}
          onAnimationComplete={(variant) => {
            const isVisible = variant === 'visible';
            const handler = isVisible ? onOpenComplete : onCloseComplete;
            handler?.();
            setTouchSafe(isVisible);
          }}
          onDragStart={(event) => {
            // cancel react-aria press event when dragging starts
            if (event.target instanceof Element) {
              getOwnerDocument(event.target).dispatchEvent(
                new PointerEvent('pointercancel')
              );
            }
          }}
          onDragEnd={(event, { offset, velocity }) => {
            if (event.type.endsWith('cancel')) return;

            // don't bother with all this logic unless the sheet is dismissable
            const sheetElement = sheetRef.current;
            if (!sheetElement || !isDismissable) return;

            // the rate of decay. more detents = less power, to avoid overshooting
            const power = 0.6 / lastIndex;
            const currentPoint = getDragPoint(currentIndex);
            const estimatedRest = currentPoint + offset.y + velocity.y * power;
            const adjacentRange = [currentIndex - 1, currentIndex + 1] as const;
            const adjacentPoints = sequence(...adjacentRange).map(getDragPoint);
            const behaviour = {
              proximity: findNearest(estimatedRest, dragPoints),
              mandatory: findNearest(estimatedRest, adjacentPoints),
            };
            const dragPoint = behaviour[snap];
            const nextIndex = dragPoints.indexOf(dragPoint);

            setDetentHeight(getSnapPoint(nextIndex));
            setCurrentIndex(nextIndex);

            if (nextIndex === 0) {
              state.close();
            } else {
              animate(
                sheetElement,
                { y: dragPoint },
                {
                  ...PHYSICAL_PROPERTIES,
                  type: 'spring',
                  velocity: velocity.y * power,
                }
              );
            }
          }}
          className={cn(
            'will-change-transform',
            'bg-canvas rounded-t-large shadow-lg',
            className
          )}
          style={{
            y: dragY,
            // why height + max-height?
            // - height is required because the content may not fill the vertical space
            // - max-height is expected by descendants, allowing layout/scrollable children to inherit
            height: SheetVars.maxHeight,
            maxHeight: SheetVars.maxHeight,
            ...SheetVars.apply({
              detentHeight,
              insetBottom: `max(${SHEET_INSET}px, env(safe-area-inset-bottom))`,
              insetTop: `max(${SHEET_INSET}px, env(safe-area-inset-top))`,
              maxHeight,
              padding: 24,
            }),
            ...style,
          }}
        >
          {composeRenderProps(props.children, (children) => (
            <>
              {children}
              <BackgroundExtension />
            </>
          ))}
        </MotionModal>
      </div>
    </MotionModalOverlay>
  );
}

// Utils
// -----------------------------------------------------------------------------

/**
 * Map unit intervals to pixel points, constrained to a min/max value.
 *
 * @returns An array of numbers representing each detent, as:
 * - `snapPoints` — The height of the sheet, in pixels.
 * - `dragPoints` — The distance from the top of the viewport to the top of the sheet, in pixels.
 */
function getPoints(detents: number[], range: [number, number], max: number) {
  const { min, round } = Math;
  const snapPoints = detents.map((x) => round(min(lerp(...range, x), max)));
  const dragPoints = snapPoints.map((x) => range[1] - x);

  return { dragPoints, snapPoints };
}

function normaliseDetents(detents: number[]) {
  if (detents.length < 1) {
    throw new Error('Detents must have at least one value.');
  }
  if (detents.some((x) => x < 0 || x > 1)) {
    throw new Error('Detents must be between 0 and 1.');
  }
  if (!isAscending(detents)) {
    throw new Error('Detents must be in ascending order.');
  }

  // zero always required (closed), don't make consumers include it explicitly
  if (detents[0] !== 0) return [0, ...detents];
  return detents;
}

// Styled components -----------------------------------------------------------

function BackgroundExtension() {
  return (
    <>
      <div
        // Account for the safe-area inset on some mobile devices. Declared
        // separately from the sheet element because it could interfere with
        // consumer provided padding.
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      />
      <div
        // Account for dragging up beyond the bottom of the viewport, and spring
        // bounce on entrance.
        className={cn(
          'h-(--height) bg-inherit',
          '[--height:100vh]',
          // -1px for rounding issues
          'mb-[calc((var(--height)-1px)*-1)]'
        )}
      />
    </>
  );
}
